import { styled } from '@linaria/react';
import { SegmentedControl, Textarea } from '@mantine/core';
import { useCallback, useEffect, useRef } from 'react';
import { useFloraUserPreferences } from '../../../../api/agents-client/agent-client.hooks';
import { ApprovalMode } from '../../../../api/agents-client/agent-client.type';
import {
  AutomationActionType,
  type EventFilterDefinition,
  type TriggerVariable,
} from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import type { SelectedMention } from '../../../../containers/flora/components/flora-mention/flora-mention.type';
import { useFeatureFlag } from '../../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../../helpers/feature-flags/feature-flags.type';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { FloraPromptTextarea } from '../shared/flora-prompt-textarea';
import type { FloraPromptTextareaHandle } from '../shared/flora-prompt-textarea';
import { inputStyles } from '../shared/input-styles';
import { formatTriggerToken, insertAtCursor } from './agent-automations.helpers';

const ACTION_TYPE_OPTIONS = [
  { label: 'Ask Flora', value: AutomationActionType.FLORA_PROMPT },
  { label: 'Static message', value: AutomationActionType.STATIC_MESSAGE },
];

// SegmentedControl needs a string value, so `null` (inherit the user's
// Customize Flora default) is represented by this sentinel in the control.
const INHERIT_APPROVAL_MODE = 'inherit';

const APPROVAL_MODE_OPTIONS = [
  { label: 'Inherit', value: INHERIT_APPROVAL_MODE },
  { label: 'Ask', value: ApprovalMode.Ask },
  { label: 'Auto', value: ApprovalMode.Auto },
];

const APPROVAL_MODE_LABELS: Record<ApprovalMode, string> = {
  [ApprovalMode.Ask]: 'Ask',
  [ApprovalMode.Auto]: 'Auto',
};

const SEGMENTED_CONTROL_STYLES = {
  root: { borderRadius: 8, background: 'var(--sky-lighter)' },
  label: { color: 'var(--ink-dark)' },
  indicator: { background: 'var(--base-white)', boxShadow: 'var(--box-shadow-small-soft)' },
};

interface AgentAutomationActionConfigProps {
  actionType: AutomationActionType;
  floraPrompt: string;
  staticMessage: string;
  eventDefinition: EventFilterDefinition | null;
  onChangeActionType: (actionType: AutomationActionType) => void;
  onChangeFloraPrompt: (prompt: string) => void;
  onChangeStaticMessage: (message: string) => void;
  onChangeFloraMentions: (mentions: SelectedMention[]) => void;
  approvalMode: ApprovalMode | null;
  onChangeApprovalMode: (approvalMode: ApprovalMode | null) => void;
}

export function AgentAutomationActionConfig({
  actionType,
  floraPrompt,
  staticMessage,
  eventDefinition,
  onChangeActionType,
  onChangeFloraPrompt,
  onChangeStaticMessage,
  onChangeFloraMentions,
  approvalMode,
  onChangeApprovalMode,
}: AgentAutomationActionConfigProps) {
  const floraPromptRef = useRef<FloraPromptTextareaHandle | null>(null);
  const messageTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // The prompt editor stays mounted across action-type toggles to preserve its
  // mention state, but its popovers render in a Portal that `display:none` on the
  // hidden section can't reach. Tear them down when the prompt editor is hidden.
  useEffect(() => {
    if (actionType !== AutomationActionType.FLORA_PROMPT) {
      floraPromptRef.current?.closePopovers();
    }
  }, [actionType]);

  const handleInsertIntoPrompt = useCallback((token: string): void => {
    floraPromptRef.current?.insertAtCursor(token);
  }, []);

  const handleInsertIntoMessage = useCallback(
    (token: string): void => {
      const element = messageTextareaRef.current;
      const result = insertAtCursor({ element, currentValue: staticMessage, token });
      onChangeStaticMessage(result.nextValue);
      requestAnimationFrame(() => {
        const el = messageTextareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(result.caretIndex, result.caretIndex);
      });
    },
    [staticMessage, onChangeStaticMessage],
  );

  return (
    <Wrapper>
      <Section>
        <SectionLabel id="automation-action-type-label">Action type</SectionLabel>
        <SegmentedControl
          value={actionType}
          onChange={(value) => onChangeActionType(value as AutomationActionType)}
          data={ACTION_TYPE_OPTIONS}
          fullWidth
          aria-labelledby="automation-action-type-label"
          styles={SEGMENTED_CONTROL_STYLES}
        />
      </Section>

      {actionType === AutomationActionType.FLORA_PROMPT && (
        <ApprovalModeSection approvalMode={approvalMode} onChangeApprovalMode={onChangeApprovalMode} />
      )}

      {/*
        Both sections stay mounted and toggle visibility (rather than a ternary)
        so switching action type does not unmount FloraPromptTextarea — that would
        drop the in-editor mention selections and persist literal `[@Name]` text.
      */}
      <ToggledSection $visible={actionType === AutomationActionType.FLORA_PROMPT}>
        <SectionLabel htmlFor="automation-prompt">Prompt</SectionLabel>
        <FloraPromptTextarea
          ref={floraPromptRef}
          id="automation-prompt"
          value={floraPrompt}
          onChange={onChangeFloraPrompt}
          onMentionsChange={onChangeFloraMentions}
          enableSlashCommands
          placeholder="What should Flora do? Type / for commands, @ to mention objects."
          minRows={6}
          maxRows={14}
        />
        <HelperText>The trigger's event data is available to Flora as context.</HelperText>
        <TriggerVariablePicker eventDefinition={eventDefinition} onInsert={handleInsertIntoPrompt} />
      </ToggledSection>

      <ToggledSection $visible={actionType === AutomationActionType.STATIC_MESSAGE}>
        <SectionLabel htmlFor="automation-static-message">Message</SectionLabel>
        <Textarea
          id="automation-static-message"
          placeholder="🚧 Item {{trigger.task_code}} moved to Blocked"
          value={staticMessage}
          onChange={(event) => onChangeStaticMessage(event.currentTarget.value)}
          minRows={5}
          autosize
          maxRows={12}
          ref={messageTextareaRef}
          styles={inputStyles}
        />
        <HelperText>Composed verbatim. No LLM call.</HelperText>
        <TriggerVariablePicker eventDefinition={eventDefinition} onInsert={handleInsertIntoMessage} />
      </ToggledSection>
    </Wrapper>
  );
}

interface ApprovalModeSectionProps {
  approvalMode: ApprovalMode | null;
  onChangeApprovalMode: (approvalMode: ApprovalMode | null) => void;
}

function ApprovalModeSection({ approvalMode, onChangeApprovalMode }: ApprovalModeSectionProps) {
  const isAutoModeEnabled = useFeatureFlag(FeatureFlag.FloraAutoMode);
  const { preferences } = useFloraUserPreferences();

  if (!isAutoModeEnabled) return null;

  const defaultApprovalMode = preferences?.default_approval_mode ?? ApprovalMode.Ask;
  const helperText = {
    [INHERIT_APPROVAL_MODE]: `Uses your default (${APPROVAL_MODE_LABELS[defaultApprovalMode]}) from Customize Flora.`,
    [ApprovalMode.Ask]: 'Actions that require approval are skipped, since no one is there to approve them.',
    [ApprovalMode.Auto]: 'Flora handles permission decisions: safe actions run, and risky ones are blocked.',
  }[approvalMode ?? INHERIT_APPROVAL_MODE];

  return (
    <Section>
      <SectionLabel id="automation-approval-mode-label">Approval mode</SectionLabel>
      <SegmentedControl
        value={approvalMode ?? INHERIT_APPROVAL_MODE}
        onChange={(value) => onChangeApprovalMode(value === INHERIT_APPROVAL_MODE ? null : (value as ApprovalMode))}
        data={APPROVAL_MODE_OPTIONS}
        fullWidth
        aria-labelledby="automation-approval-mode-label"
        styles={SEGMENTED_CONTROL_STYLES}
      />
      <HelperText>{helperText}</HelperText>
    </Section>
  );
}

interface TriggerVariablePickerProps {
  eventDefinition: EventFilterDefinition | null;
  onInsert: (token: string) => void;
}

function TriggerVariablePicker({ eventDefinition, onInsert }: TriggerVariablePickerProps) {
  if (!eventDefinition) {
    return (
      <PickerWrapper>
        <PickerHeader>
          <Icon name="data_object" color="var(--ink-lighter)" size={14} />
          <Text size="tiny" weight="bold" color="var(--ink-lighter)" style={{ letterSpacing: '0.05em' }}>
            VARIABLES — CLICK TO INSERT
          </Text>
        </PickerHeader>
        <Text size="tiny" color="var(--ink-lighter)" style={{ fontStyle: 'italic' }}>
          Pick a trigger first to see variables.
        </Text>
      </PickerWrapper>
    );
  }

  const variables = eventDefinition.trigger_variables ?? [];
  if (variables.length === 0) {
    return (
      <PickerWrapper>
        <PickerHeader>
          <Icon name="data_object" color="var(--ink-lighter)" size={14} />
          <Text size="tiny" weight="bold" color="var(--ink-lighter)" style={{ letterSpacing: '0.05em' }}>
            VARIABLES — CLICK TO INSERT
          </Text>
        </PickerHeader>
        <Text size="tiny" color="var(--ink-lighter)" style={{ fontStyle: 'italic' }}>
          This trigger doesn't expose any variables.
        </Text>
      </PickerWrapper>
    );
  }

  return (
    <PickerWrapper>
      <PickerHeader>
        <Icon name="data_object" color="var(--ink-lighter)" size={14} />
        <Text size="tiny" weight="bold" color="var(--ink-lighter)" style={{ letterSpacing: '0.05em' }}>
          VARIABLES — CLICK TO INSERT
        </Text>
      </PickerHeader>
      <ChipGroup>
        {variables.map((variable) => (
          <VariableChip key={variable.path} variable={variable} onInsert={onInsert} />
        ))}
      </ChipGroup>
    </PickerWrapper>
  );
}

interface VariableChipProps {
  variable: TriggerVariable;
  onInsert: (token: string) => void;
}

function VariableChip({ variable, onInsert }: VariableChipProps) {
  const token = formatTriggerToken(variable.path);
  const tooltipBody = (
    <>
      {variable.description && (
        <Text size="small" color="inherit">
          {variable.description}
        </Text>
      )}
      <Text size="tiny" color="inherit" style={{ marginTop: variable.description ? 4 : 0 }}>
        {token}
      </Text>
      {variable.example && (
        <Text size="tiny" color="inherit" style={{ marginTop: 2, opacity: 0.8 }}>
          Example: {variable.example}
        </Text>
      )}
    </>
  );
  return (
    <Tooltip
      variant="black"
      label={tooltipBody}
      position="top"
      withinPortal
      multiline
      withArrow
      openDelay={120}
      maw={280}
    >
      <Chip type="button" onClick={() => onInsert(token)}>
        {variable.label}
      </Chip>
    </Tooltip>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-dark);
`;

const HelperText = styled.span`
  font-size: 12px;
  color: var(--ink-lighter);
`;

// A Section that hides via `display:none` (not unmount) when inactive, so
// toggling the action type preserves the Flora prompt's mention state. Mirrors
// `Section`'s flex layout when visible.
const ToggledSection = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  flex-direction: column;
  gap: 8px;
`;

const PickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border-radius: 10px;
  border: 1px dashed var(--sky-light);
  background: var(--sky-lightest);
`;

const PickerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  cursor: pointer;
  font-family: var(--font-mono, monospace);
  font-size: 11.5px;
  font-weight: 600;
  color: var(--secondary-base);
  transition: background 0.12s, border-color 0.12s;

  &:hover {
    background: var(--secondary-lightest, rgba(87, 92, 229, 0.1));
    border-color: var(--secondary-base);
  }
`;

export type { AgentAutomationActionConfigProps };
